import React from 'react';
import dayjs from './dayjs';
import { ReceiptGenerator, ExchangeReceipt, pdf, SalesExports, SaleReturnExports, ExchangeExports, DamageDispatchExport } from 'accoutre-react-pdf';
import {config} from '../config/env';
import { useAppStore } from '../stores/useAppStore';

// Prints a sale receipt using the accoutre-react-pdf components
// Accepts a full `sale` object and handles PDF generation and printing
export const printSaleReceipt = async (sale) => {

  console.log("config.appIcon ", config.appIcon)
  
  if (!sale) throw new Error('printSaleReceipt: missing sale');

  // Get business information from store
  const { businessInfo } = useAppStore.getState();

  const items = (sale.details || []).map((detail) => {
    return {
      description: detail?.item?.Name,
      price: Number(detail?.Price || 0),
      quantity: Number(detail?.Quantity || 0),
      amount:
        Number(detail?.Quantity || 0) * Number(detail?.Price || 0) -
        Number(detail?.Discount || 0) * Number(detail?.Quantity || 0),
      discount: Number(detail?.Discount || 0) * Number(detail?.Quantity || 0),
    };
  });

  const subTotal = items.reduce((acc, item) => acc + (item.price * item.quantity), 0);
  let productDiscount = items.reduce((acc, item) => acc + item.discount, 0);
  // apply additional discount which was given by the cashier

  const netAmount = subTotal - (productDiscount + (sale.Discount || 0));

  const dateStr = sale.SaleDate ? dayjs(sale.SaleDate).format('DD MMM YYYY') : '-';
  const timeStr = sale.SaleDate ? dayjs(sale.SaleDate).format('hh:mm A') : '-';

  const receiptData = {
    Sale: {
      SaleNo: sale.SaleNo,
    },
    company: {
      logo: 'AC',
      name: businessInfo?.BusinessName || config.appTitle,
      address: businessInfo?.BusinessAddress || 'Climate Plaza , Street 36 , G13/2',
      phone: businessInfo?.BusinessPhone || '+92303 0941111',
    },
    transaction: {
      date: dateStr,
      time: timeStr,
      salesman: 'Counter Sale',
      invoiceNumber: sale.SaleNo,
    },
    items,
    totals: {
      subTotal,
      additionalDiscount: sale.Discount || 0,
      totalDiscount: productDiscount,
      netAmount: Math.round(netAmount),
      bankAmount: sale.BankReceived,
      cashAmount: sale.CashReceived,
      cashReturn: sale.CashReturned,
    },
    terms: [
      'All Prices Are Fixed',
      'Please retain original receipt for exchange and refund within 14 days of purchase.',
      'For exchange and refund all items must be in original condition with all tabs and label intact.',
      'Articles worn/washed/shrunk will not be exchanged and refunded.',
      'Please read and follow washing instructions on the care label carefully.'
    ],
    developer: {
      name: 'Esper Solutions',
      website: 'www.esper.solutions',
    },
  };

  console.log("receipt data being passed ", receiptData)

  // Generate PDF blob
  const blob = await pdf(
    <ReceiptGenerator 
      receiptData={receiptData} 
      logoSrc={config.appIcon} 
      validDays={14}
      companyName={businessInfo?.BusinessName}
      companyAddress={businessInfo?.BusinessAddress}
      companyPhone={businessInfo?.BusinessPhone}
    />
  ).toBlob();

  // Print within the same tab using an invisible iframe
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.src = url;
  document.body.appendChild(iframe);

  iframe.onload = () => {
    const w = iframe.contentWindow;
    if (!w) return;

    // Add CSS to force actual size printing with minimal margins
    const style = w.document.createElement('style');
    style.textContent = `
      @media print {
        @page {
          size: A4;
          margin: 0.05in 0.1in;
        }
        html, body {
          width: 100% !important;
          height: auto !important;
          transform: none !important;
          zoom: 1 !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        body > div {
          width: 100% !important;
          max-width: none !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        * {
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        /* Optimize receipt content for thermal printers */
        .receipt-container {
          width: 100% !important;
          max-width: none !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        .receipt-content {
          width: 100% !important;
          margin: 0 auto !important;
          padding: 0 !important;
        }
        /* Force PDF content to use full width */
        canvas, svg, iframe {
          width: 100% !important;
          max-width: none !important;
        }
        /* Optimize for thermal receipt printers */
        @media print and (max-width: 4in) {
          @page {
            size: 3.5in auto;
            margin: 0.05in;
          }
        }
      }
    `;
    w.document.head.appendChild(style);

    // Add additional styling for better thermal printer optimization
    const thermalStyle = w.document.createElement('style');
    thermalStyle.textContent = `
      @media print {
        body {
          margin: 0 !important;
          padding: 0 !important;
          width: 100% !important;
        }
        /* Target the PDF viewer specifically */
        embed, object, iframe {
          width: 100% !important;
          height: auto !important;
          border: none !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        /* Ensure PDF content scales properly */
        .pdf-viewer, .pdf-container {
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        }
      }
    `;
    w.document.head.appendChild(thermalStyle);

    const cleanup = () => {
      try {
        document.body.removeChild(iframe);
      } catch {}
      URL.revokeObjectURL(url);
    };

    // Prefer cleaning up after the print dialog is closed
    w.onafterprint = () => {
      cleanup();
      w.onafterprint = null;
    };

    // Fallback: in case onafterprint doesn't fire on some browsers
    setTimeout(() => {
      try {
        w.onafterprint = null;
      } catch {}
      cleanup();
    }, 60000); // 60s safety net

    w.focus();
    w.print();
  };
};

// Prints an exchange receipt using the accoutre-react-pdf ExchangeReceipt component
// Accepts a full `exchange` object (matching backend schema) and handles PDF generation and printing
export const printExchangeReceipt = async (exchange) => {
  if (!exchange) throw new Error('printExchangeReceipt: missing exchange');

  // Get business information from store
  const { businessInfo } = useAppStore.getState();

  // Create PDF with the ExchangeReceipt component; component handles formatting and math internally
  const blob = await pdf(
    <ExchangeReceipt 
      data={exchange} 
      logoSrc={config.appIcon} 
      validDays={14}
      companyName={businessInfo?.BusinessName}
      companyAddress={businessInfo?.BusinessAddress}
      companyPhone={businessInfo?.BusinessPhone}
    />
  ).toBlob();

  // Print within the same tab using an invisible iframe (same flow as sales receipt)
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.src = url;
  document.body.appendChild(iframe);

  iframe.onload = () => {
    const w = iframe.contentWindow;
    if (!w) return;

    const style = w.document.createElement('style');
    style.textContent = `
      @media print {
        @page { size: A4; margin: 0.05in 0.1in; }
        html, body { width: 100% !important; height: auto !important; transform: none !important; zoom: 1 !important; margin: 0 !important; padding: 0 !important; }
        body > div { width: 100% !important; max-width: none !important; margin: 0 !important; padding: 0 !important; }
        * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; }
        .receipt-container, .receipt-content, .pdf-viewer, .pdf-container { width: 100% !important; margin: 0 !important; padding: 0 !important; }
        canvas, svg, iframe { width: 100% !important; max-width: none !important; }
        @media print and (max-width: 4in) { @page { size: 3.5in auto; margin: 0.05in; } }
      }
    `;
    w.document.head.appendChild(style);

    const thermalStyle = w.document.createElement('style');
    thermalStyle.textContent = `
      @media print {
        body { margin: 0 !important; padding: 0 !important; width: 100% !important; }
        embed, object, iframe { width: 100% !important; height: auto !important; border: none !important; margin: 0 !important; padding: 0 !important; }
      }
    `;
    w.document.head.appendChild(thermalStyle);

    const cleanup = () => {
      try { document.body.removeChild(iframe); } catch {}
      URL.revokeObjectURL(url);
    };

    w.onafterprint = () => { cleanup(); w.onafterprint = null; };
    setTimeout(() => { try { w.onafterprint = null; } catch {}; cleanup(); }, 60000);

    w.focus();
    w.print();
  };
};

export const printSalesExport = async (salesData) => {
  // Get business information from store
  const { businessInfo } = useAppStore.getState();
  
  const blob = await pdf(<SalesExports 
    logoSrc={config.appIcon}   
    salesData={salesData}
    businessName={businessInfo?.BusinessName}
    businessAddress={businessInfo?.BusinessAddress}
    businessPhone={businessInfo?.BusinessPhone}
  />).toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `sales-export-${salesData.dateRange.start}-to-${salesData.dateRange.end}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const printReturnsExport = async (returnsData) => {
  // Get business information from store
  const { businessInfo } = useAppStore.getState();
  
  const blob = await pdf(<SaleReturnExports       
    logoSrc={config.appIcon}  
    returnsData={returnsData}
    businessName={businessInfo?.BusinessName}
    businessAddress={businessInfo?.BusinessAddress}
    businessPhone={businessInfo?.BusinessPhone}
  />).toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `returns-export-${returnsData.dateRange.start}-to-${returnsData.dateRange.end}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const printExchangeReport = async (exchangeData) => {
  // Get business information from store
  const { businessInfo } = useAppStore.getState();
  
  const blob = await pdf(<ExchangeExports       
    logoSrc={config.appIcon}  
    exchangeData={exchangeData}
    businessName={businessInfo?.BusinessName}
    businessAddress={businessInfo?.BusinessAddress}
    businessPhone={businessInfo?.BusinessPhone}
  />).toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `exchange-export-${exchangeData.dateRange.start}-to-${exchangeData.dateRange.end}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Damages dispatch: downloads the list that goes to the warehouse with the damaged stock.
export const printDamageDispatch = async (dispatch, formatPrice) => {
  const { businessInfo } = useAppStore.getState();
  const when = (d) => (d ? dayjs(d).format('DD MMM YYYY, h:mm A') : '');
  const firstRecorded = dispatch.items?.[0]?.CreatedDate;
  const periodText = dispatch.PeriodStart
    ? `${dayjs(dispatch.PeriodStart).format('DD MMM YYYY')} to ${dayjs(dispatch.DispatchDate).format('DD MMM YYYY')}`
    : `Up to ${dayjs(dispatch.DispatchDate).format('DD MMM YYYY')}${firstRecorded ? ` (from ${dayjs(firstRecorded).format('DD MMM YYYY')})` : ''}`;

  const blob = await pdf(<DamageDispatchExport
    logoSrc={config.appIcon}
    businessName={businessInfo?.BusinessName}
    branchName={businessInfo?.BranchLocation?.trim()}
    businessAddress={businessInfo?.BusinessAddress}
    businessPhone={businessInfo?.BusinessPhone}
    dispatchNo={dispatch.DispatchNo}
    dispatchDate={when(dispatch.DispatchDate)}
    periodText={periodText}
    sentBy={dispatch.CreatedByName}
    remarks={dispatch.Remarks}
    totalQuantity={dispatch.TotalQuantity}
    totalValue={formatPrice(dispatch.TotalValue)}
    lines={(dispatch.items || []).map((d) => ({
      name: d.ItemName || `Item #${d.Item_ID}`,
      barcode: d.Barcode,
      quantity: d.Quantity,
      reason: d.Reason,
      remarks: d.Remarks,
      recorded: [when(d.CreatedDate), d.CreatedByName].filter(Boolean).join(' - '),
      value: formatPrice(d.LineValue),
    }))}
  />).toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `damages-${dispatch.DispatchNo}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
